import type { Core } from '@pdftron/webviewer';

// PDF action types that are pure, local navigation with no outbound network
// effect - these are the only ones allowed to auto-trigger (e.g. from an
// /OpenAction) without going through our blocking handler.
const SAFE_ACTION_NAMES = ['GoTo', 'Named'];

// Members Core.Actions exposes alongside the action classes that are NOT
// themselves triggerable actions, so they must not be passed to
// setCustomOnTriggeredHandler.
const NON_ACTION_MEMBER_NAMES = ['Dispatcher', 'setCustomOnTriggeredHandler'];

// Action types a correctly-configured policy must always end up blocking.
// Used as a runtime canary: if any of these are missing from
// `blockedActionClasses`, the Apryse SDK likely reshaped `Core.Actions` in a
// way our reflection-based policy no longer understands, and the check
// should fail loudly rather than silently allowing these through.
export const REQUIRED_BLOCKED_ACTION_NAMES = ['SubmitForm', 'URI', 'GoToR'];

type ActionsNamespace = typeof Core.Actions;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ActionClass = new (...args: any[]) => Core.Actions.Action;

export interface ActionSecurityPolicy {
  safeActionClasses: ActionClass[];
  blockedActionClasses: ActionClass[];
  // The SDK's minified build mangles each action class's `.name`, so we
  // recover a stable, human-readable name from the property key it's
  // exposed under on Core.Actions (e.g. "SubmitForm") instead.
  nameByActionClass: Map<ActionClass, string>;
}

/**
 * Computes a deny-by-default policy over every action class exposed on
 * `Core.Actions`: everything is blocked unless explicitly allowlisted as
 * side-effect-free navigation. This is deliberately reflection-based (rather
 * than hardcoding SubmitForm/URI) so an action type added in a future Apryse
 * SDK version is blocked automatically instead of silently allowed.
 *
 * Note: `Core.Actions` has no base `Action` class exposed at runtime despite
 * what the SDK's type definitions claim (confirmed against the actual
 * compiled bundle), so subclass detection can't use `instanceof` - we
 * instead treat any function-valued member that isn't a known non-action
 * utility (see NON_ACTION_MEMBER_NAMES) as an action class.
 */
export function computeActionSecurityPolicy(
  actions: ActionsNamespace,
): ActionSecurityPolicy {
  const record = actions as unknown as Record<string, unknown>;

  const actionEntries = Object.entries(record).filter(
    (entry): entry is [string, ActionClass] =>
      typeof entry[1] === 'function' &&
      !NON_ACTION_MEMBER_NAMES.includes(entry[0]),
  );

  const nameByActionClass = new Map(
    actionEntries.map(([name, actionClass]) => [actionClass, name] as const),
  );

  const safeActionClasses = actionEntries
    .filter(([name]) => SAFE_ACTION_NAMES.includes(name))
    .map(([, actionClass]) => actionClass);

  const blockedActionClasses = actionEntries
    .filter(([name]) => !SAFE_ACTION_NAMES.includes(name))
    .map(([, actionClass]) => actionClass);

  return { safeActionClasses, blockedActionClasses, nameByActionClass };
}

/**
 * Verifies the computed policy actually blocks the action types known to be
 * exploitable (see PP-26001). Calls `onUnsound` if any are missing, e.g. to
 * report to an error tracker, rather than throwing - a failure here means
 * the SDK's shape changed, not that the document being viewed is malicious.
 */
export function assertActionSecurityPolicyIsSound(
  policy: Pick<
    ActionSecurityPolicy,
    'blockedActionClasses' | 'nameByActionClass'
  >,
  onUnsound: (message: string, missing: string[]) => void,
): void {
  const blockedNames = new Set(
    policy.blockedActionClasses.map(
      (actionClass) => policy.nameByActionClass.get(actionClass) ?? '<unknown>',
    ),
  );
  const missing = REQUIRED_BLOCKED_ACTION_NAMES.filter(
    (name) => !blockedNames.has(name),
  );
  if (missing.length > 0) {
    onUnsound(
      `PDF viewer action security policy failed to block required action types: ${missing.join(
        ', ',
      )}. The Apryse WebViewer SDK likely changed the shape of Core.Actions.`,
      missing,
    );
  }
}
